'use client';

import {
	AlertTriangle,
	BadgeCheck,
	CheckCircle2,
	ClipboardCheck,
	Clock3,
	FileText,
	Plus,
	RefreshCw,
	ShieldCheck,
	Trash2,
	UserPlus
} from 'lucide-react';
import type { ComponentType, FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { api, getErrorMessage } from '$lib/api/client';
import type {
	CreateEmployeeLifecycleCaseRequest,
	CreateEmployeeLifecycleDocumentRequest,
	CreateEmployeeLifecycleTaskRequest,
	CreateEmployeeLifecycleTemplateRequest,
	EmployeeLifecycleCaseProgress,
	EmployeeLifecycleCaseStatus,
	EmployeeLifecycleChecklistItem,
	EmployeeLifecycleChecklistStatus,
	EmployeeLifecycleDocumentRequestItem,
	EmployeeLifecycleDocumentStatus,
	EmployeeLifecyclePlaybook,
	EmployeeLifecycleRiskItem,
	EmployeeLifecycleRiskSeverity,
	EmployeeLifecycleSummaryResponse,
	EmployeeLifecycleTemplateTaskItem,
	EmployeeLifecycleTaskItem,
	EmployeeLifecycleTaskSource,
	EmployeeLifecycleTaskStatus,
	EmployeeLifecycleTemplateItem,
	EmployeeLifecycleType
} from '$lib/api/types';
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	EmptyState,
	Input,
	Label,
	PageHeader,
	Spinner
} from '$lib/ui';

type RequestState<T> =
	| { status: 'idle' | 'loading' }
	| { status: 'success'; data: T }
	| { status: 'error'; message: string };

type TemplateFormState = {
	type: EmployeeLifecycleType;
	name: string;
	description: string;
	defaultOwnerRole: string;
	autoShareProfile: boolean;
	items: TemplateItemFormState[];
};

type TemplateItemFormState = {
	title: string;
	ownerName: string;
	dueOffsetDays: string;
	profileShared: boolean;
};

type CaseFormState = {
	type: EmployeeLifecycleType;
	employeeId: string;
	employeeName: string;
	templateName: string;
	targetDate: string;
	ownerName: string;
};

type TaskFormState = {
	caseId: string;
	title: string;
	ownerName: string;
	dueDate: string;
	profileShared: boolean;
	source: EmployeeLifecycleTaskSource;
};

type DocumentFormState = {
	caseId: string;
	documentName: string;
	ownerName: string;
	dueDate: string;
	memo: string;
};

function numberLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '0';
	return new Intl.NumberFormat('ko-KR').format(value);
}

function defaultDate(days = 14): string {
	const date = new Date();
	date.setDate(date.getDate() + days);
	return date.toISOString().slice(0, 10);
}

function dateLabel(value: string | null | undefined): string {
	if (!value) return '-';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat('ko-KR', { month: '2-digit', day: '2-digit' }).format(date);
}

function dueOffsetLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return 'D-day';
	if (value === 0) return 'D-day';
	return value < 0 ? `D${value}` : `D+${value}`;
}

function defaultTemplateItems(type: EmployeeLifecycleType): TemplateItemFormState[] {
	if (type === 'OFFBOARDING') {
		return [
			{
				title: '접근권한 회수 예약',
				ownerName: 'IT Admin',
				dueOffsetDays: '-3',
				profileShared: true
			}
		];
	}
	return [
		{
			title: '근로계약서/입사 서류 발송',
			ownerName: 'People Ops',
			dueOffsetDays: '-7',
			profileShared: true
		}
	];
}

function lifecycleTypeLabel(type: EmployeeLifecycleType | null | undefined): string {
	if (type === 'ONBOARDING') return '입사';
	if (type === 'OFFBOARDING') return '퇴사';
	return '-';
}

function caseStatusLabel(status: EmployeeLifecycleCaseStatus): string {
	const labels: Record<EmployeeLifecycleCaseStatus, string> = {
		IN_PROGRESS: '진행중',
		COMPLETED: '완료',
		CANCELLED: '취소'
	};
	return labels[status] ?? status;
}

function taskStatusLabel(status: EmployeeLifecycleTaskStatus): string {
	const labels: Record<EmployeeLifecycleTaskStatus, string> = {
		TODO: '대기',
		IN_PROGRESS: '진행중',
		DONE: '완료',
		BLOCKED: '차단'
	};
	return labels[status] ?? status;
}

function documentStatusLabel(status: EmployeeLifecycleDocumentStatus): string {
	const labels: Record<EmployeeLifecycleDocumentStatus, string> = {
		REQUESTED: '요청중',
		RECEIVED: '수령',
		WAIVED: '면제'
	};
	return labels[status] ?? status;
}

function sourceLabel(source: EmployeeLifecycleTaskSource): string {
	const labels: Record<EmployeeLifecycleTaskSource, string> = {
		TEMPLATE: '템플릿',
		MANUAL: '수동',
		DOCUMENT: '문서',
		SYSTEM: '시스템'
	};
	return labels[source] ?? source;
}

function checklistStatusLabel(status: EmployeeLifecycleChecklistStatus): string {
	if (status === 'READY') return '준비';
	if (status === 'WARNING') return '주의';
	return '대기';
}

function checklistVariant(status: EmployeeLifecycleChecklistStatus): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function riskVariant(severity: EmployeeLifecycleRiskSeverity): 'success' | 'warning' | 'outline' {
	if (severity === 'READY') return 'success';
	if (severity === 'WARNING') return 'warning';
	return 'outline';
}

function statusVariant(
	status: EmployeeLifecycleCaseStatus | EmployeeLifecycleTaskStatus
): 'success' | 'warning' | 'outline' {
	if (status === 'DONE' || status === 'COMPLETED') return 'success';
	if (status === 'TODO' || status === 'IN_PROGRESS' || status === 'BLOCKED') return 'warning';
	return 'outline';
}

function documentStatusVariant(status: EmployeeLifecycleDocumentStatus): 'success' | 'warning' | 'outline' {
	if (status === 'RECEIVED') return 'success';
	if (status === 'REQUESTED') return 'warning';
	return 'outline';
}

function StatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: number | string;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="flex min-h-32 flex-col justify-between p-4">
				<div className="flex items-center justify-between gap-3">
					<p className="text-xs font-medium text-muted-foreground">{title}</p>
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
				</div>
				<div>
					<p className="text-2xl font-semibold tracking-normal">
						{typeof value === 'number' ? numberLabel(value) : value}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

function ChecklistGrid({ items }: { items: EmployeeLifecycleChecklistItem[] }) {
	if (items.length === 0) return <EmptyState message="입퇴사 체크리스트 준비 상태가 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
			{items.map((item) => {
				const Icon = item.status === 'READY' ? CheckCircle2 : item.status === 'WARNING' ? AlertTriangle : Clock3;
				return (
					<div key={item.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
						<div className="flex items-start justify-between gap-3">
							<div className="flex min-w-0 items-start gap-3">
								<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
									<Icon className="size-4" />
								</div>
								<div className="min-w-0">
									<p className="font-semibold">{item.label}</p>
									<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
								</div>
							</div>
							<Badge variant={checklistVariant(item.status)}>{checklistStatusLabel(item.status)}</Badge>
						</div>
					</div>
				);
			})}
		</div>
	);
}

function RiskPanel({ risks }: { risks: EmployeeLifecycleRiskItem[] }) {
	if (risks.length === 0) return <EmptyState message="표시할 입퇴사 리스크가 없습니다." />;
	return (
		<div className="min-w-0 rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">입퇴사 리스크</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={riskVariant(risk.severity)}>{numberLabel(risk.count)}</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function CaseTable({ cases }: { cases: EmployeeLifecycleCaseProgress[] }) {
	if (cases.length === 0) return <EmptyState message="진행 중인 입퇴사 케이스가 없습니다." />;
	return (
		<div className="max-w-full overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[760px] text-left text-sm">
				<thead className="bg-muted/60 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-medium">대상자</th>
						<th className="px-4 py-3 font-medium">템플릿</th>
						<th className="px-4 py-3 font-medium">목표일</th>
						<th className="px-4 py-3 font-medium">진척</th>
						<th className="px-4 py-3 font-medium">상태</th>
					</tr>
				</thead>
				<tbody>
					{cases.map((lifecycleCase) => (
						<tr key={lifecycleCase.caseId} className="border-t border-border align-top">
							<td className="px-4 py-4">
								<div className="flex flex-wrap items-center gap-2">
									<p className="font-semibold">{lifecycleCase.employeeName}</p>
									<Badge variant="outline">{lifecycleTypeLabel(lifecycleCase.type)}</Badge>
								</div>
								<p className="mt-1 text-xs text-muted-foreground">{lifecycleCase.ownerName}</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">{lifecycleCase.templateName}</td>
							<td className="px-4 py-4 text-muted-foreground">{dateLabel(lifecycleCase.targetDate)}</td>
							<td className="px-4 py-4">
								<p className="font-semibold">{numberLabel(lifecycleCase.progressPercent)}%</p>
								<p className="mt-1 text-xs text-muted-foreground">
									{numberLabel(lifecycleCase.completedTaskCount)} / {numberLabel(lifecycleCase.totalTaskCount)}개
								</p>
							</td>
							<td className="px-4 py-4">
								<div className="flex flex-wrap items-center gap-2">
									<Badge variant={statusVariant(lifecycleCase.status)}>{caseStatusLabel(lifecycleCase.status)}</Badge>
									<Badge variant={lifecycleCase.healthLabel === '주의' ? 'warning' : 'outline'}>
										{lifecycleCase.healthLabel}
									</Badge>
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function TaskTable({
	tasks,
	actionId,
	onComplete
}: {
	tasks: EmployeeLifecycleTaskItem[];
	actionId: number | null;
	onComplete: (task: EmployeeLifecycleTaskItem) => void;
}) {
	if (tasks.length === 0) return <EmptyState message="입퇴사 담당 태스크가 없습니다." />;
	return (
		<div className="max-w-full overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[820px] text-left text-sm">
				<thead className="bg-muted/60 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-medium">태스크</th>
						<th className="px-4 py-3 font-medium">담당</th>
						<th className="px-4 py-3 font-medium">마감</th>
						<th className="px-4 py-3 font-medium">출처</th>
						<th className="px-4 py-3 font-medium">작업</th>
					</tr>
				</thead>
				<tbody>
					{tasks.map((task) => (
						<tr key={task.id} className="border-t border-border align-top">
							<td className="px-4 py-4">
								<div className="flex flex-wrap items-center gap-2">
									<p className="font-semibold">{task.title}</p>
									<Badge variant={statusVariant(task.status)}>{taskStatusLabel(task.status)}</Badge>
								</div>
								<p className="mt-1 text-xs text-muted-foreground">
									{task.employeeName ?? '-'} · {lifecycleTypeLabel(task.type)}
								</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">{task.ownerName ?? '미지정'}</td>
							<td className="px-4 py-4">
								<p className="text-muted-foreground">{dateLabel(task.dueDate)}</p>
								<Badge className="mt-2" variant={task.healthLabel === '마감 초과' ? 'warning' : 'outline'}>
									{task.healthLabel}
								</Badge>
							</td>
							<td className="px-4 py-4">
								<div className="flex flex-wrap items-center gap-2">
									<Badge variant="outline">{sourceLabel(task.source)}</Badge>
									{task.profileShared ? <Badge variant="success">정보 공유</Badge> : null}
								</div>
							</td>
							<td className="px-4 py-4">
								<Button
									type="button"
									size="sm"
									variant="outline"
									disabled={task.status === 'DONE' || actionId === task.id}
									onClick={() => onComplete(task)}
								>
									<BadgeCheck className="size-4" />
									완료
								</Button>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function DocumentRequestTable({
	documents,
	actionId,
	onReceive
}: {
	documents: EmployeeLifecycleDocumentRequestItem[];
	actionId: number | null;
	onReceive: (documentRequest: EmployeeLifecycleDocumentRequestItem) => void;
}) {
	if (documents.length === 0) return <EmptyState message="요청 중인 입퇴사 서류가 없습니다." />;
	return (
		<div className="max-w-full overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[760px] text-left text-sm">
				<thead className="bg-muted/60 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-medium">서류명</th>
						<th className="px-4 py-3 font-medium">대상자</th>
						<th className="px-4 py-3 font-medium">담당</th>
						<th className="px-4 py-3 font-medium">마감</th>
						<th className="px-4 py-3 font-medium">작업</th>
					</tr>
				</thead>
				<tbody>
					{documents.map((documentRequest) => (
						<tr key={documentRequest.id} className="border-t border-border align-top">
							<td className="px-4 py-4">
								<div className="flex flex-wrap items-center gap-2">
									<p className="font-semibold">{documentRequest.documentName}</p>
									<Badge variant={documentStatusVariant(documentRequest.status)}>
										{documentStatusLabel(documentRequest.status)}
									</Badge>
								</div>
								<p className="mt-1 text-xs text-muted-foreground">케이스 #{documentRequest.caseId}</p>
							</td>
							<td className="px-4 py-4">
								<p className="font-medium">{documentRequest.employeeName ?? '-'}</p>
								<p className="mt-1 text-xs text-muted-foreground">{lifecycleTypeLabel(documentRequest.type)}</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">{documentRequest.ownerName ?? '미지정'}</td>
							<td className="px-4 py-4">
								<p className="text-muted-foreground">{dateLabel(documentRequest.dueDate)}</p>
								<Badge className="mt-2" variant={documentRequest.healthLabel === '마감 초과' ? 'warning' : 'outline'}>
									{documentRequest.healthLabel}
								</Badge>
							</td>
							<td className="px-4 py-4">
								<Button
									type="button"
									size="sm"
									variant="outline"
									disabled={documentRequest.status === 'RECEIVED' || actionId === documentRequest.id}
									onClick={() => onReceive(documentRequest)}
								>
									<CheckCircle2 className="size-4" />
									수령 완료
								</Button>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function TemplateGrid({ templates }: { templates: EmployeeLifecycleTemplateItem[] }) {
	if (templates.length === 0) return <EmptyState message="등록된 체크리스트 템플릿이 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2">
			{templates.map((template) => (
				<div key={template.id} className="min-w-0 rounded-lg border border-border bg-background p-4">
					<div className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<div className="flex flex-wrap items-center gap-2">
								<p className="font-semibold">{template.name}</p>
								<Badge variant="outline">{lifecycleTypeLabel(template.type)}</Badge>
							</div>
							<p className="mt-2 text-sm text-muted-foreground">{template.description ?? '-'}</p>
							<div className="mt-3 flex flex-wrap gap-2">
								<Badge variant="outline">{numberLabel(template.itemCount)}개 할 일</Badge>
								<Badge variant="outline">{template.defaultOwnerRole}</Badge>
								{template.autoShareProfile ? <Badge variant="success">정보 자동 공유</Badge> : null}
							</div>
							{template.items.length > 0 ? <TemplateItemPreview items={template.items} /> : null}
						</div>
						<Badge variant={template.active ? 'success' : 'outline'}>{template.active ? '활성' : '비활성'}</Badge>
					</div>
				</div>
			))}
		</div>
	);
}

function TemplateItemPreview({ items }: { items: EmployeeLifecycleTemplateTaskItem[] }) {
	return (
		<div className="mt-4 space-y-2">
			{items.slice(0, 4).map((item, index) => (
				<div
					key={`${item.title}-${index}`}
					className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-md bg-muted/30 px-3 py-2"
				>
					<div className="min-w-0">
						<p className="truncate text-sm font-medium">{item.title}</p>
						<p className="mt-0.5 text-xs text-muted-foreground">{item.ownerName ?? '기본 담당'}</p>
					</div>
					<div className="flex shrink-0 items-center gap-2">
						<Badge variant="outline">{dueOffsetLabel(item.dueOffsetDays)}</Badge>
						{item.profileShared ? <Badge variant="success">정보 공유</Badge> : null}
					</div>
				</div>
			))}
			{items.length > 4 ? <p className="text-xs text-muted-foreground">외 {numberLabel(items.length - 4)}개 항목</p> : null}
		</div>
	);
}

function PlaybookGrid({ playbooks }: { playbooks: EmployeeLifecyclePlaybook[] }) {
	if (playbooks.length === 0) return <EmptyState message="입퇴사 운영 플레이북이 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
			{playbooks.map((playbook) => (
				<div key={playbook.code} className="min-w-0 rounded-lg border border-border bg-background p-4">
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<ShieldCheck className="size-4" />
					</div>
					<p className="mt-3 font-semibold">{playbook.label}</p>
					<p className="mt-1 text-sm text-muted-foreground">{playbook.description}</p>
				</div>
			))}
		</div>
	);
}

export function EmployeeLifecyclePage() {
	const [state, setState] = useState<RequestState<EmployeeLifecycleSummaryResponse>>({ status: 'idle' });
	const [actionMessage, setActionMessage] = useState<string | null>(null);
	const [operationLoading, setOperationLoading] = useState<string | null>(null);
	const [taskActionId, setTaskActionId] = useState<number | null>(null);
	const [documentActionId, setDocumentActionId] = useState<number | null>(null);
	const [templateForm, setTemplateForm] = useState<TemplateFormState>({
		type: 'ONBOARDING',
		name: '',
		description: '',
		defaultOwnerRole: 'People Ops',
		autoShareProfile: true,
		items: defaultTemplateItems('ONBOARDING')
	});
	const [caseForm, setCaseForm] = useState<CaseFormState>({
		type: 'ONBOARDING',
		employeeId: '',
		employeeName: '',
		templateName: '',
		targetDate: defaultDate(),
		ownerName: 'People Ops'
	});
	const [taskForm, setTaskForm] = useState<TaskFormState>({
		caseId: '',
		title: '',
		ownerName: '',
		dueDate: defaultDate(3),
		profileShared: true,
		source: 'TEMPLATE'
	});
	const [documentForm, setDocumentForm] = useState<DocumentFormState>({
		caseId: '',
		documentName: '',
		ownerName: 'People Ops',
		dueDate: defaultDate(7),
		memo: ''
	});

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const summary = await api.get<EmployeeLifecycleSummaryResponse>('/employee-lifecycle/summary');
				setState({ status: 'success', data: summary });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;
	const caseTemplateOptions =
		data?.templateLibrary.filter((template) => template.active && template.type === caseForm.type) ?? [];
	const selectedCaseTemplate =
		caseTemplateOptions.find((template) => template.name === caseForm.templateName.trim()) ?? null;

	function selectCaseTemplate(templateId: string) {
		const template = caseTemplateOptions.find((item) => String(item.id) === templateId);
		if (!template) {
			setCaseForm((current) => ({ ...current, templateName: '' }));
			return;
		}
		setCaseForm((current) => ({
			...current,
			type: template.type,
			templateName: template.name,
			ownerName: template.defaultOwnerRole
		}));
	}

	function updateTemplateType(type: EmployeeLifecycleType) {
		setTemplateForm((current) => ({
			...current,
			type,
			defaultOwnerRole: type === 'ONBOARDING' ? 'People Ops' : 'HR Admin',
			items: defaultTemplateItems(type)
		}));
	}

	function updateTemplateItem(index: number, next: Partial<TemplateItemFormState>) {
		setTemplateForm((current) => ({
			...current,
			items: current.items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...next } : item))
		}));
	}

	function addTemplateItem() {
		setTemplateForm((current) => ({
			...current,
			items: [
				...current.items,
				{
					title: '',
					ownerName: current.defaultOwnerRole,
					dueOffsetDays: '0',
					profileShared: current.autoShareProfile
				}
			]
		}));
	}

	function removeTemplateItem(index: number) {
		setTemplateForm((current) => ({
			...current,
			items: current.items.length <= 1 ? current.items : current.items.filter((_, itemIndex) => itemIndex !== index)
		}));
	}

	async function submitTemplate(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setOperationLoading('template');
		setActionMessage(null);
		try {
			const items = templateForm.items
				.map((item) => ({
					title: item.title.trim(),
					ownerName: item.ownerName.trim() || null,
					dueOffsetDays: Number(item.dueOffsetDays),
					profileShared: item.profileShared
				}))
				.filter((item) => item.title.length > 0);
			if (items.length === 0 || items.some((item) => !Number.isFinite(item.dueOffsetDays))) {
				setActionMessage('체크리스트 항목과 D-day 기준을 입력해주세요.');
				return;
			}
			const request: CreateEmployeeLifecycleTemplateRequest = {
				type: templateForm.type,
				name: templateForm.name.trim(),
				description: templateForm.description.trim() || null,
				itemCount: items.length,
				defaultOwnerRole: templateForm.defaultOwnerRole.trim(),
				autoShareProfile: templateForm.autoShareProfile,
				items
			};
			await api.post('/employee-lifecycle/templates', request);
			setTemplateForm((current) => ({ ...current, name: '', description: '', items: defaultTemplateItems(current.type) }));
			setActionMessage('체크리스트 템플릿을 생성했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setOperationLoading(null);
		}
	}

	async function submitCase(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setOperationLoading('case');
		setActionMessage(null);
		try {
			const request: CreateEmployeeLifecycleCaseRequest = {
				type: caseForm.type,
				employeeId: Number(caseForm.employeeId),
				employeeName: caseForm.employeeName.trim(),
				templateName: caseForm.templateName.trim(),
				targetDate: caseForm.targetDate,
				ownerName: caseForm.ownerName.trim()
			};
			await api.post('/employee-lifecycle/cases', request);
			setCaseForm((current) => ({ ...current, employeeId: '', employeeName: '', targetDate: defaultDate() }));
			setActionMessage(
				selectedCaseTemplate
					? `입퇴사 케이스를 시작했습니다. 표준 태스크 ${numberLabel(selectedCaseTemplate.itemCount)}개가 자동 생성됩니다.`
					: '입퇴사 케이스를 시작했습니다.'
			);
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setOperationLoading(null);
		}
	}

	async function submitTask(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setOperationLoading('task');
		setActionMessage(null);
		try {
			const request: CreateEmployeeLifecycleTaskRequest = {
				title: taskForm.title.trim(),
				ownerName: taskForm.ownerName.trim() || null,
				dueDate: taskForm.dueDate,
				profileShared: taskForm.profileShared,
				source: taskForm.source
			};
			await api.post(`/employee-lifecycle/cases/${Number(taskForm.caseId)}/tasks`, request);
			setTaskForm((current) => ({ ...current, title: '', dueDate: defaultDate(3) }));
			setActionMessage('담당 태스크를 추가했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setOperationLoading(null);
		}
	}

	async function submitDocumentRequest(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setOperationLoading('document');
		setActionMessage(null);
		try {
			const request: CreateEmployeeLifecycleDocumentRequest = {
				documentName: documentForm.documentName.trim(),
				ownerName: documentForm.ownerName.trim() || null,
				dueDate: documentForm.dueDate,
				memo: documentForm.memo.trim() || null
			};
			await api.post(`/employee-lifecycle/cases/${Number(documentForm.caseId)}/documents`, request);
			setDocumentForm((current) => ({ ...current, documentName: '', dueDate: defaultDate(7), memo: '' }));
			setActionMessage('요청 서류를 추가했습니다. 문서 회수 태스크가 자동 생성됩니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setOperationLoading(null);
		}
	}

	async function completeTask(task: EmployeeLifecycleTaskItem) {
		setTaskActionId(task.id);
		setActionMessage(null);
		try {
			await api.patch(`/employee-lifecycle/tasks/${task.id}/complete`);
			setActionMessage('담당 태스크를 완료했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setTaskActionId(null);
		}
	}

	async function receiveDocumentRequest(documentRequest: EmployeeLifecycleDocumentRequestItem) {
		setDocumentActionId(documentRequest.id);
		setActionMessage(null);
		try {
			await api.patch(`/employee-lifecycle/document-requests/${documentRequest.id}/receive`);
			setActionMessage('요청 서류를 수령 처리했습니다.');
			await load();
		} catch (error) {
			setActionMessage(getErrorMessage(error));
		} finally {
			setDocumentActionId(null);
		}
	}

	return (
		<div className="min-w-0 space-y-6 overflow-x-hidden">
			<PageHeader
				title="입퇴사"
				description="입사·퇴사 체크리스트 템플릿, 담당자, 마감일, 구성원 정보 공유와 진행상황을 한 화면에서 관리합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{actionMessage ? (
				<div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">{actionMessage}</div>
			) : null}

			{data ? (
				<>
					<div className="grid min-w-0 grid-cols-2 gap-3 lg:grid-cols-5">
						<StatCard
							title="진행 케이스"
							value={data.activeCaseCount}
							description={`전체 ${numberLabel(data.totalCaseCount)}건`}
							icon={ClipboardCheck}
						/>
						<StatCard
							title="입사 / 퇴사"
							value={`${numberLabel(data.onboardingCaseCount)} / ${numberLabel(data.offboardingCaseCount)}`}
							description="진행 대상자 구성"
							icon={UserPlus}
						/>
						<StatCard
							title="태스크 완료"
							value={`${numberLabel(data.completedTaskCount)} / ${numberLabel(data.totalTaskCount)}`}
							description={`마감 초과 ${numberLabel(data.overdueTaskCount)}건`}
							icon={BadgeCheck}
						/>
						<StatCard
							title="정보 공유"
							value={data.profileSharedTaskCount}
							description={`템플릿 ${numberLabel(data.templateCount)}개`}
							icon={FileText}
						/>
						<StatCard
							title="서류 수령"
							value={`${numberLabel(data.receivedDocumentRequestCount)} / ${numberLabel(data.totalDocumentRequestCount)}`}
							description={`미수령 ${numberLabel(data.pendingDocumentRequestCount)}건 · 초과 ${numberLabel(data.overdueDocumentRequestCount)}건`}
							icon={FileText}
						/>
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-4">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>템플릿 생성</CardTitle>
								<CardDescription>입사·퇴사 업무를 반복 가능한 체크리스트로 표준화합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<form className="grid gap-3" onSubmit={submitTemplate}>
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="lifecycle-template-type">유형</Label>
											<select
												id="lifecycle-template-type"
												className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
												value={templateForm.type}
												onChange={(event) =>
													updateTemplateType(event.target.value as EmployeeLifecycleType)
												}
											>
												<option value="ONBOARDING">입사</option>
												<option value="OFFBOARDING">퇴사</option>
											</select>
										</div>
										<div>
											<Label htmlFor="lifecycle-template-owner">기본 담당</Label>
											<Input
												id="lifecycle-template-owner"
												value={templateForm.defaultOwnerRole}
												onChange={(event) =>
													setTemplateForm((current) => ({ ...current, defaultOwnerRole: event.target.value }))
												}
												required
											/>
										</div>
									</div>
									<div>
										<Label htmlFor="lifecycle-template-name">템플릿명</Label>
										<Input
											id="lifecycle-template-name"
											value={templateForm.name}
											onChange={(event) => setTemplateForm((current) => ({ ...current, name: event.target.value }))}
											placeholder="예: 신규 입사자 표준 온보딩"
											required
										/>
									</div>
									<div>
										<Label htmlFor="lifecycle-template-description">설명</Label>
										<textarea
											id="lifecycle-template-description"
											className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 min-h-20 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
											value={templateForm.description}
											onChange={(event) =>
												setTemplateForm((current) => ({ ...current, description: event.target.value }))
											}
										/>
									</div>
									<div className="grid gap-3 sm:grid-cols-2">
										<label className="flex items-end gap-2 pb-2 text-sm">
											<input
												type="checkbox"
												checked={templateForm.autoShareProfile}
												onChange={(event) =>
													setTemplateForm((current) => ({
														...current,
														autoShareProfile: event.target.checked
													}))
												}
											/>
											정보 자동 공유
										</label>
									</div>
									<div className="space-y-3">
										<div className="flex items-center justify-between gap-3">
											<p className="text-sm font-semibold">체크리스트 항목</p>
											<Button type="button" size="sm" variant="outline" onClick={addTemplateItem}>
												<Plus className="size-4" />
												항목 추가
											</Button>
										</div>
										{templateForm.items.map((item, index) => (
											<div key={index} className="rounded-lg border border-border bg-muted/20 p-3">
												<div className="grid gap-3">
													<div>
														<Label htmlFor={`lifecycle-template-item-title-${index}`}>
															항목 {index + 1} 할 일
														</Label>
														<Input
															id={`lifecycle-template-item-title-${index}`}
															value={item.title}
															onChange={(event) => updateTemplateItem(index, { title: event.target.value })}
															placeholder="예: 보안 서약서 제출"
															required
														/>
													</div>
													<div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_120px]">
														<div>
															<Label htmlFor={`lifecycle-template-item-owner-${index}`}>
																항목 {index + 1} 담당
															</Label>
															<Input
																id={`lifecycle-template-item-owner-${index}`}
																value={item.ownerName}
																onChange={(event) => updateTemplateItem(index, { ownerName: event.target.value })}
																placeholder={templateForm.defaultOwnerRole}
															/>
														</div>
														<div>
															<Label htmlFor={`lifecycle-template-item-offset-${index}`}>
																항목 {index + 1} D-day 기준
															</Label>
															<Input
																id={`lifecycle-template-item-offset-${index}`}
																type="number"
																value={item.dueOffsetDays}
																onChange={(event) => updateTemplateItem(index, { dueOffsetDays: event.target.value })}
																required
															/>
														</div>
													</div>
													<div className="flex flex-wrap items-center justify-between gap-3">
														<label className="flex items-center gap-2 text-sm">
															<input
																type="checkbox"
																checked={item.profileShared}
																onChange={(event) =>
																	updateTemplateItem(index, { profileShared: event.target.checked })
																}
															/>
															항목 {index + 1} 정보 공유
														</label>
														<Button
															type="button"
															size="sm"
															variant="outline"
															disabled={templateForm.items.length <= 1}
															onClick={() => removeTemplateItem(index)}
														>
															<Trash2 className="size-4" />
															삭제
														</Button>
													</div>
												</div>
											</div>
										))}
									</div>
									<Button type="submit" disabled={operationLoading === 'template'}>
										<Plus className="size-4" />
										템플릿 생성
									</Button>
								</form>
							</CardContent>
						</Card>

						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>케이스 시작</CardTitle>
								<CardDescription>입사자 또는 퇴사자별로 템플릿과 목표일을 연결합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<form className="grid gap-3" onSubmit={submitCase}>
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="lifecycle-case-type">유형</Label>
											<select
												id="lifecycle-case-type"
												className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
												value={caseForm.type}
												onChange={(event) =>
													setCaseForm((current) => ({
														...current,
														type: event.target.value as EmployeeLifecycleType
													}))
												}
											>
												<option value="ONBOARDING">입사</option>
												<option value="OFFBOARDING">퇴사</option>
											</select>
										</div>
										<div>
											<Label htmlFor="lifecycle-case-employee-id">직원 ID</Label>
											<Input
												id="lifecycle-case-employee-id"
												type="number"
												value={caseForm.employeeId}
												onChange={(event) =>
													setCaseForm((current) => ({ ...current, employeeId: event.target.value }))
												}
												required
											/>
										</div>
									</div>
									<div>
										<Label htmlFor="lifecycle-case-name">대상자</Label>
										<Input
											id="lifecycle-case-name"
											value={caseForm.employeeName}
											onChange={(event) => setCaseForm((current) => ({ ...current, employeeName: event.target.value }))}
											required
										/>
									</div>
									<div>
										{caseTemplateOptions.length > 0 ? (
											<div className="mb-3">
												<Label htmlFor="lifecycle-case-template-select">템플릿 선택</Label>
												<select
													id="lifecycle-case-template-select"
													className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
													value={selectedCaseTemplate ? String(selectedCaseTemplate.id) : ''}
													onChange={(event) => selectCaseTemplate(event.target.value)}
												>
													<option value="">직접 입력</option>
													{caseTemplateOptions.map((template) => (
														<option key={template.id} value={template.id}>
															{template.name}
														</option>
													))}
												</select>
											</div>
										) : null}
										<Label htmlFor="lifecycle-case-template">케이스 템플릿명</Label>
										<Input
											id="lifecycle-case-template"
											value={caseForm.templateName}
											onChange={(event) =>
												setCaseForm((current) => ({ ...current, templateName: event.target.value }))
											}
											required
										/>
									</div>
									{selectedCaseTemplate ? (
										<div className="rounded-lg border border-border bg-muted/20 p-4">
											<div className="flex flex-wrap items-start justify-between gap-3">
												<div>
													<p className="font-semibold">표준 태스크 {numberLabel(selectedCaseTemplate.itemCount)}개 자동 생성</p>
													<p className="mt-1 text-sm text-muted-foreground">
														기본 담당 {selectedCaseTemplate.defaultOwnerRole}
													</p>
												</div>
												<Badge variant={selectedCaseTemplate.autoShareProfile ? 'success' : 'outline'}>
													{selectedCaseTemplate.autoShareProfile ? '정보 자동 공유' : '정보 공유 안 함'}
												</Badge>
											</div>
										</div>
									) : null}
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="lifecycle-case-target">목표일</Label>
											<Input
												id="lifecycle-case-target"
												type="date"
												value={caseForm.targetDate}
												onChange={(event) =>
													setCaseForm((current) => ({ ...current, targetDate: event.target.value }))
												}
												required
											/>
										</div>
										<div>
											<Label htmlFor="lifecycle-case-owner">담당자</Label>
											<Input
												id="lifecycle-case-owner"
												value={caseForm.ownerName}
												onChange={(event) =>
													setCaseForm((current) => ({ ...current, ownerName: event.target.value }))
												}
												required
											/>
										</div>
									</div>
									<Button type="submit" disabled={operationLoading === 'case'}>
										<UserPlus className="size-4" />
										케이스 시작
									</Button>
								</form>
							</CardContent>
						</Card>

							<Card className="min-w-0">
								<CardHeader>
									<CardTitle>태스크 추가</CardTitle>
								<CardDescription>케이스별 담당 업무, 마감일, 프로필 공유 여부를 기록합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<form className="grid gap-3" onSubmit={submitTask}>
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="lifecycle-task-case">케이스 ID</Label>
											<Input
												id="lifecycle-task-case"
												type="number"
												value={taskForm.caseId}
												onChange={(event) => setTaskForm((current) => ({ ...current, caseId: event.target.value }))}
												required
											/>
										</div>
										<div>
											<Label htmlFor="lifecycle-task-source">출처</Label>
											<select
												id="lifecycle-task-source"
												className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
												value={taskForm.source}
												onChange={(event) =>
													setTaskForm((current) => ({
														...current,
														source: event.target.value as EmployeeLifecycleTaskSource
													}))
												}
											>
												<option value="TEMPLATE">템플릿</option>
												<option value="MANUAL">수동</option>
												<option value="DOCUMENT">문서</option>
												<option value="SYSTEM">시스템</option>
											</select>
										</div>
									</div>
									<div>
										<Label htmlFor="lifecycle-task-title">할 일</Label>
										<Input
											id="lifecycle-task-title"
											value={taskForm.title}
											onChange={(event) => setTaskForm((current) => ({ ...current, title: event.target.value }))}
											placeholder="예: 근로계약서 발송"
											required
										/>
									</div>
									<div className="grid gap-3 sm:grid-cols-2">
										<div>
											<Label htmlFor="lifecycle-task-owner">담당자</Label>
											<Input
												id="lifecycle-task-owner"
												value={taskForm.ownerName}
												onChange={(event) => setTaskForm((current) => ({ ...current, ownerName: event.target.value }))}
											/>
										</div>
										<div>
											<Label htmlFor="lifecycle-task-due">마감일</Label>
											<Input
												id="lifecycle-task-due"
												type="date"
												value={taskForm.dueDate}
												onChange={(event) => setTaskForm((current) => ({ ...current, dueDate: event.target.value }))}
												required
											/>
										</div>
									</div>
									<label className="flex items-center gap-2 text-sm">
										<input
											type="checkbox"
											checked={taskForm.profileShared}
											onChange={(event) =>
												setTaskForm((current) => ({ ...current, profileShared: event.target.checked }))
											}
										/>
										대상자 기본 정보 공유
									</label>
									<Button type="submit" disabled={operationLoading === 'task'}>
										<Plus className="size-4" />
										태스크 추가
									</Button>
									</form>
								</CardContent>
							</Card>

							<Card className="min-w-0">
								<CardHeader>
									<CardTitle>서류 요청</CardTitle>
									<CardDescription>입사·퇴사 케이스에 제출 서류를 요청하고 회수 태스크를 자동 생성합니다.</CardDescription>
								</CardHeader>
								<CardContent>
									<form className="grid gap-3" onSubmit={submitDocumentRequest}>
										<div>
											<Label htmlFor="lifecycle-document-case">서류 케이스 ID</Label>
											<Input
												id="lifecycle-document-case"
												type="number"
												value={documentForm.caseId}
												onChange={(event) =>
													setDocumentForm((current) => ({ ...current, caseId: event.target.value }))
												}
												required
											/>
										</div>
										<div>
											<Label htmlFor="lifecycle-document-name">요청 서류명</Label>
											<Input
												id="lifecycle-document-name"
												value={documentForm.documentName}
												onChange={(event) =>
													setDocumentForm((current) => ({ ...current, documentName: event.target.value }))
												}
												placeholder="예: 주민등록등본"
												required
											/>
										</div>
										<div className="grid gap-3 sm:grid-cols-2">
											<div>
												<Label htmlFor="lifecycle-document-owner">서류 담당자</Label>
												<Input
													id="lifecycle-document-owner"
													value={documentForm.ownerName}
													onChange={(event) =>
														setDocumentForm((current) => ({ ...current, ownerName: event.target.value }))
													}
												/>
											</div>
											<div>
												<Label htmlFor="lifecycle-document-due">서류 마감일</Label>
												<Input
													id="lifecycle-document-due"
													type="date"
													value={documentForm.dueDate}
													onChange={(event) =>
														setDocumentForm((current) => ({ ...current, dueDate: event.target.value }))
													}
													required
												/>
											</div>
										</div>
										<div>
											<Label htmlFor="lifecycle-document-memo">서류 메모</Label>
											<textarea
												id="lifecycle-document-memo"
												className="border-input bg-background ring-offset-background focus-visible:ring-ring/35 min-h-20 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
												value={documentForm.memo}
												onChange={(event) =>
													setDocumentForm((current) => ({ ...current, memo: event.target.value }))
												}
											/>
										</div>
										<Button type="submit" disabled={operationLoading === 'document'}>
											<Plus className="size-4" />
											서류 요청
										</Button>
									</form>
								</CardContent>
							</Card>
						</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,0.65fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>운영 체크리스트</CardTitle>
								<CardDescription>템플릿, 담당자, 프로필 공유, 마감일, 퇴사 통제가 준비되었는지 확인합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<ChecklistGrid items={data.checklist} />
							</CardContent>
						</Card>
						<RiskPanel risks={data.riskItems} />
					</div>

					<div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>케이스 진행상황</CardTitle>
								<CardDescription>입사자와 퇴사자별 담당자, 목표일, 완료율을 추적합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<CaseTable cases={data.caseProgress} />
							</CardContent>
						</Card>
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>담당 태스크</CardTitle>
								<CardDescription>마감 초과, 담당자 누락, 정보 공유 여부를 확인하고 완료 처리합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<TaskTable
									tasks={data.taskBoard}
									actionId={taskActionId}
									onComplete={(task) => void completeTask(task)}
								/>
							</CardContent>
						</Card>
					</div>

					<Card className="min-w-0">
						<CardHeader>
							<CardTitle>요청 서류</CardTitle>
							<CardDescription>입퇴사 대상자별 제출 요청, 마감일, 수령 상태를 추적합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<DocumentRequestTable
								documents={data.documentRequests}
								actionId={documentActionId}
								onReceive={(documentRequest) => void receiveDocumentRequest(documentRequest)}
							/>
						</CardContent>
					</Card>

					<Card className="min-w-0">
						<CardHeader>
							<CardTitle>템플릿 라이브러리</CardTitle>
							<CardDescription>반복되는 입사·퇴사 운영 절차를 템플릿으로 관리합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<TemplateGrid templates={data.templateLibrary} />
						</CardContent>
					</Card>

					<Card className="min-w-0">
						<CardHeader>
							<CardTitle>운영 플레이북</CardTitle>
							<CardDescription>입사 전 준비부터 퇴사 보안 통제와 지식 이전까지 표준 운영 루틴을 확인합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<PlaybookGrid playbooks={data.playbooks} />
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}
